import { useTranslation } from 'react-i18next';
import { UserAvatar } from '@/shared/components/custom/avatars/UserAvatar';
import { getUserColor } from '@/core/lib/get-user-color';
import { formatDisplayTime } from '@/shared/utils/dateUtils';
import { buildActivityMessage } from '../../utils/activity-message';
import { CATEGORY_ICON } from '../../utils/board-activity-category';
import { useTaskDialogStore } from '../../hooks/useTaskDialogStore';
import type { ActivityDto, TaskActivityCategory } from '../../types';
import { TASK_ACTIVITY_CATEGORY } from '../../types';

interface BoardActivityFeedItemProps {
  activity: ActivityDto;
}

export function BoardActivityFeedItem({ activity }: BoardActivityFeedItemProps) {
  const { t } = useTranslation('tasks');
  const openTask = useTaskDialogStore((s) => s.openTask);

  const actorName = activity.actorName ?? '';
  const { actionText, detail } = buildActivityMessage(activity, t);
  const CategoryIcon =
    CATEGORY_ICON[activity.category as TaskActivityCategory] ??
    CATEGORY_ICON[TASK_ACTIVITY_CATEGORY.General];

  return (
    <div
      className="flex items-start gap-3 rounded-lg border border-border bg-background px-3.5 py-3"
      data-testid="tasks-board-activity-item"
    >
      <div className="relative shrink-0">
        <UserAvatar
          name={actorName}
          color={activity.actorColor ?? getUserColor(activity.actorId ?? '')}
          src={activity.actorAvatarUrl ?? undefined}
          size="sm"
        />
        <span className="absolute -bottom-1 -right-1 flex size-4 items-center justify-center rounded-lg border border-border bg-background">
          <CategoryIcon className="size-2.5 text-muted-foreground" />
        </span>
      </div>

      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-x-1 text-[13.5px]">
          <span className="font-medium text-foreground">{actorName}</span>
          <span className="text-muted-foreground">{actionText}</span>
          {activity.taskId && (
            <button
              type="button"
              onClick={() => openTask(activity.taskId!)}
              className="font-medium text-primary hover:underline"
              data-testid="tasks-board-activity-item-link"
              title={t('activity.feed.viewTask')}
            >
              {activity.taskTitle}
            </button>
          )}
        </div>

        {detail != null && <p className="mt-0.5 text-[12.5px] text-muted-foreground">{detail}</p>}
      </div>

      <p className="shrink-0 whitespace-nowrap text-xs text-muted-foreground">
        {formatDisplayTime(activity.createdAt)}
      </p>
    </div>
  );
}
